import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Sprite, Container, Text } from "@pixi/react";
import { Assets, ColorMatrixFilter, DisplayObject, TextStyle } from "pixi.js";
import BuildingStatus from "./BuildingStatus";
import BuildingMarker from "./BuildingMarker";
import LoadingIndicator from "../../components/LoadingIndicator";
import { BuildingApiType, PlayerBuildingState } from "../../types/buildings";
import { SoundUtils } from "../../utils/soundUtils";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "@pixi/gif";
import { AnimatedGIF } from "@pixi/gif";

interface BuildingDecoration {
  image: string;
  offsetX: number;
  offsetY: number;
  scale?: number;
  anchor?: number | { x: number; y: number };
  zIndex?: number;
}

interface BuildingType {
  id: number;
  /** Sprite shown once the building is constructed (state !== NotHave). */
  builtImg: string;
  /** Sprite shown while the building is an empty plot (state === NotHave). */
  notBuiltImg: string;
  x: number;
  y: number;
  scale: number;
  name: string;
  level: number;
  namePosition?: { x: number; y: number };
  progress: number;
  icon?: string;
  type: "static" | "dynamic";
  decorations?: BuildingDecoration[];
}

// Combined type for building with both static and API data
interface CombinedBuilding extends BuildingType, BuildingApiType {
  apiId?: number;
}

interface AlphaHitMap {
  width: number;
  height: number;
  alpha: Uint8Array;
}

const BUILDING_SPRITE_ANCHOR = 0.5;
const ALPHA_HIT_THRESHOLD = 8;
const alphaHitMapCache = new Map<string, Promise<AlphaHitMap | null>>();

const loadAlphaHitMap = (src: string) => {
  const cached = alphaHitMapCache.get(src);
  if (cached) return cached;

  const promise = new Promise<AlphaHitMap | null>((resolve) => {
    if (typeof document === "undefined") {
      resolve(null);
      return;
    }

    const image = new Image();
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;

      if (!width || !height) {
        resolve(null);
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");
      if (!context) {
        resolve(null);
        return;
      }

      try {
        context.drawImage(image, 0, 0, width, height);
        const pixels = context.getImageData(0, 0, width, height).data;
        const alpha = new Uint8Array(width * height);

        for (
          let pixelIndex = 0, alphaIndex = 3;
          pixelIndex < alpha.length;
          pixelIndex += 1, alphaIndex += 4
        ) {
          alpha[pixelIndex] = pixels[alphaIndex];
        }

        resolve({ width, height, alpha });
      } catch {
        resolve(null);
      }
    };

    image.onerror = () => resolve(null);
    image.src = src;
  });

  alphaHitMapCache.set(src, promise);
  return promise;
};

interface BuildingProps {
  building: BuildingType;
  highlight: number | null;
  buildingData: BuildingApiType;
  handleSpriteOver: (id: number) => (event: any) => void;
  handleSpriteOut: () => void;
  handleSpriteTap: (id: number) => (event: any) => void;
  onCollect?: (id: number | string) => void;
  onRepair?: (id: number | string) => void;
  onInstantUpgrade?: (id: number | string) => void;
  onOpenBuildDialog?: (buildingId: number) => void;
  onOpenUpgradeDialog?: (buildingId: number) => void;
  onOpenRepairDialog?: (buildingId: number) => void;
  isCollecting?: boolean;
  soundVolume?: number;
}

const Building: React.FC<BuildingProps> = ({
  building,
  highlight,
  buildingData,
  handleSpriteOver,
  handleSpriteOut,
  handleSpriteTap,
  onCollect,
  onRepair,
  onInstantUpgrade,
  onOpenBuildDialog,
  onOpenUpgradeDialog,
  isCollecting = false,
  soundVolume = 0.5,
}) => {
  const { t } = useAppTranslation(["village"]);
  const containerRef = useRef<any>(null);
  const pointerDownTime = useRef<number>(0);

  // Snow filter - subtle desaturation and cool tone, applied to every building
  // so the new art blends with the winter map.
  const snowFilter = new ColorMatrixFilter();
  snowFilter.saturate(-0.38); // Reduce saturation by 25% for subtle effect
  snowFilter.brightness(1.10, true); // Slightly brighten

  // Buildings that have not been constructed yet (state === NotHave) render a
  // dedicated "empty plot" sprite; every other state renders the built sprite.
  const isNotBuilt = buildingData?.state === PlayerBuildingState.NotHave;
  const spriteImage = isNotBuilt ? building.notBuiltImg : building.builtImg;
  const [alphaHitMap, setAlphaHitMap] = useState<AlphaHitMap | null>(null);

  useEffect(() => {
    let isCurrent = true;

    setAlphaHitMap(null);
    loadAlphaHitMap(spriteImage).then((hitMap) => {
      if (isCurrent) {
        setAlphaHitMap(hitMap);
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [spriteImage]);

  const alphaHitArea = useMemo(() => {
    if (!alphaHitMap) return null;

    const { width, height, alpha } = alphaHitMap;

    return {
      contains: (x: number, y: number) => {
        const textureX = x + width * BUILDING_SPRITE_ANCHOR;
        const textureY = y + height * BUILDING_SPRITE_ANCHOR;

        if (
          textureX < 0 ||
          textureY < 0 ||
          textureX >= width ||
          textureY >= height
        ) {
          return false;
        }

        const pixelX = Math.floor(textureX);
        const pixelY = Math.floor(textureY);

        return alpha[pixelY * width + pixelX] > ALPHA_HIT_THRESHOLD;
      },
    };
  }, [alphaHitMap]);

  const combinedData = buildingData
    ? ({
        ...building,
        ...buildingData,
        apiId: buildingData.id,
      } as CombinedBuilding)
    : (building as unknown as CombinedBuilding);

  // useEffect(() => {
  //   // (1) optional: register under a key, so you don’t have to type the URL twice
  //   Assets.add("zzzGif", "/assets/images/map/zzz.gif");

  //   const loadGifAnimation = async () => {
  //     try {
  //       // (2) load by alias
  //       const gifResource = await Assets.load<AnimatedGIF>("zzzGif");
  //       if (gifResource.constructor.name === "_AnimatedGIF") {
  //         // (3) clone + configure
  //         const anim = gifResource.clone ? gifResource.clone() : gifResource;
  //         anim.width = 45;
  //         anim.height = 50;
  //         anim.animationSpeed = 1.5;
  //         anim.loop = true;
  //         anim.autoUpdate = true;
  //         setZzzAnimation(anim);
  //       }
  //     } catch (e) {
  //       console.error("Error loading GIF:", e);
  //     }
  //   };

  //   loadGifAnimation();
  // }, [building.id]);
  // Debug: Log which buildings should show the GIF
  const shouldShowGif = combinedData.state === PlayerBuildingState.Ready;

  // Add the AnimatedGIF to the container when it's loaded
  // useEffect(() => {
  //   if (zzzAnimation && containerRef.current) {
  //     containerRef.current.addChild(zzzAnimation);

  //     // Cleanup function to remove the child when component unmounts
  //     return () => {
  //       if (
  //         containerRef.current &&
  //         zzzAnimation.parent === containerRef.current
  //       ) {
  //         containerRef.current.removeChild(zzzAnimation);
  //       }
  //     };
  //   }
  // }, [zzzAnimation]);
  const onSpritePointerDown = () => {
    pointerDownTime.current = Date.now();
  };

  // Called when they lift (inside or outside) the sprite
  const onSpritePointerUp = (event: PIXI.FederatedPointerEvent) => {
    const elapsed = Date.now() - pointerDownTime.current;
    if (elapsed < 200) {
      // Quick tap — treat as click!
      handleBuildingClick(event);
    }
    // else: it was a long press / drag → ignore
  };

  // Handle building sprite click - special logic for collectable buildings
  const handleBuildingClick = (event: any) => {
    // Play building click sound effect
    if (soundVolume > 0) {
      SoundUtils.playBuildingSound(combinedData.buildingId||combinedData.id, soundVolume);
    }

    // Check if building has id: 0 (needs to be built) - prioritize this check
    if (buildingData?.id === 0) {
      // Building with id: 0 means it needs to be built - use default handler to trigger build modal
      handleSpriteTap(building.id)(event);
      return;
    }

    const collectableBuildings = [3, 4, 5]; // Resource buildings

    if (collectableBuildings.includes(combinedData.buildingId)) {
      // For collectable buildings (3, 4, 5)
      if (buildingData?.state === PlayerBuildingState.NotHave) {
        // User doesn't have the building - use default handler to show build dialog
        handleSpriteTap(building.id)(event);
      } else if (isCollectable && onCollect) {
        // Building is collectable - collect from it
        onCollect(combinedData.id);
      } else {
        // For other states (like upgrading), use default handler
        handleSpriteTap(building.id)(event);
      }
    } else {
      // For non-collectable buildings, use default handler
      handleSpriteTap(building.id)(event);
    }
  };

  // Check if building is ready for collection
  const isCollectable =
    buildingData &&
    buildingData.state !== 0 &&
    buildingData.buildingId !== 6 &&
    buildingData.currentMaxCapacity > 0 &&
    (buildingData.currentCapacity * 100) / buildingData.currentMaxCapacity > 20;

  // Check if building needs repair - state 3 is Down
  const needsRepair =
    buildingData &&
    buildingData.state === PlayerBuildingState.Down &&
    buildingData.buildingId !== 6; // Exclude barracks
  const needsBuild = buildingData?.state === PlayerBuildingState.NotHave;

  // Calculate fill percentage (ensure we're not dividing by zero)
  const fillPercentage =
    combinedData.currentMaxCapacity && combinedData.currentCapacity
      ? Math.min(
          100,
          Math.max(
            0,
            (buildingData?.currentCapacity * 100) /
              buildingData?.currentMaxCapacity
          )
        )
      : 0;

  // Marker actions mirror the building itself: hammer markers open the build
  // or repair flow, while a full resource marker collects its resources.
  const handleMarkerClick = useCallback(
    (event: PIXI.FederatedPointerEvent) => {
      event.stopPropagation();

      // Play building click sound effect
      if (soundVolume > 0) {
        try {
          SoundUtils.playBuildingSound(combinedData.buildingId, soundVolume);
        } catch {}
      }

      if (needsBuild || needsRepair) {
        handleSpriteTap(building.id)(event);
        return;
      }

      if (isCollectable && onCollect && combinedData.id) {
        onCollect(combinedData.id);
      }
    },
    [
      building.id,
      combinedData.id,
      combinedData.buildingId,
      needsBuild,
      needsRepair,
      handleSpriteTap,
      isCollectable,
      onCollect,
      soundVolume,
    ]
  );

  const staticNameTextStyle = new TextStyle({
    fontFamily: "changa",
    fontSize: 20,
    fontWeight: "bold",
    fill: 0xf2f2f2,
    stroke: 0x000000,
    strokeThickness: 4,

  });

  const spritePointerOver = handleSpriteOver(building.id);

  return (
    <>
      <Sprite
        zIndex={building.id === 6 || building.id === 3 ? 100 : 0}
        image={spriteImage}
        x={building.x}
        y={building.y}
        scale={building.scale}
        anchor={0.5}
        eventMode={building.id === 21 ? "none" : "static"}
        hitArea={alphaHitArea}
        cursor={building.id === 21 ? "default" : "pointer"}
        tint={
          highlight === building.id
            ? 0xffffaa // Yellow tint for highlighted buildings
            : 0xffffff // Normal white tint
        }
        onpointerover={spritePointerOver}
        onpointermove={spritePointerOver}
        onpointerout={handleSpriteOut}
        onpointerup={onSpritePointerUp}
        onpointerdown={onSpritePointerDown}
        onpointerupoutside={onSpritePointerUp}
      />
      {building.decorations?.map((decoration, index) => (
        <Sprite
          key={`building-decoration-${building.id}-${index}`}
          image={decoration.image}
          x={building.x + decoration.offsetX}
          y={building.y + decoration.offsetY}
          scale={decoration.scale ?? 1}
          anchor={decoration.anchor ?? 0.5}
          zIndex={decoration.zIndex ?? 0}
          eventMode="none"
        />
      ))}
      {building.type === "dynamic" && (
        <>
          <BuildingStatus
            x={building.x - 40}
            y={building.y + 100}
            scale={0.5}
            isDungeon={building.id === 12}
            trainingQueue={combinedData?.trainingQueue}
            maxTrainingQueue={combinedData.maxQueue}
            name={combinedData.name}
            level={combinedData.currentLevel || building.level}
            progress={combinedData.currentHealth || building.progress}
            capacity={combinedData.currentMaxCapacity || 0}
            filled={combinedData.currentCapacity || 0}
            isCollectable={isCollectable}
            health={combinedData.currentHealth}
            isBarracks={combinedData.buildingId === 6}
            buildingState={buildingData?.state}
            upgradeTime={combinedData?.nextEventDate}
            buildingId={combinedData.buildingId}
            buildingData={combinedData}
            onInstantUpgrade={() => onInstantUpgrade?.(combinedData.apiId!)}
          />
          {building.id !== 7 &&
            building.id !== 12 &&
            buildingData?.state !== PlayerBuildingState.Upgrading && (
              <BuildingMarker
                x={building.x}
                y={building.y - 100}
                scale={0.5}
                buildingState={buildingData?.state}
                upgradeIcon="/assets/images/icons/upgrade-arrow.png"
                buildingIcon={building?.icon}
                onClick={
                  isCollectable || needsRepair || needsBuild
                    ? handleMarkerClick
                    : undefined
                }
                trainingQueue={buildingData?.trainingQueue}
                maxTrainingQueue={buildingData?.maxQueue}
                isHighlighted={isCollectable}
                fillPercentage={fillPercentage}
                needsRepair={needsRepair}
                needBuild={needsBuild}
                isBarracks={buildingData?.buildingId === 6}
                repairIcon="/assets/images/icons/repair.png"
                buildIcon="/assets/images/icons/build-icon.png"
              />
            )}
          {/* ZZZ Animation Container - only show when building is ready */}
          {/* {shouldShowGif && (
            <Container
              ref={containerRef}
              x={building.x + 50}
              y={building.y - 100}
            />
          )} */}

          {combinedData.buildingId !== 6 &&  buildingData?.state===PlayerBuildingState.Ready && (
            <Sprite
              image="/assets/images/icons/zzz.png"
              x={building.x + 50}
              y={building.y - 100}
              zIndex={400}
            />
          )}

          {/* Loading indicator for collection */}
          <LoadingIndicator
            x={building.x}
            y={building.y - 50}
            scale={0.8}
            isVisible={isCollecting}
            text={t("village:status.collecting")}
            color={0x4ade80} // Green color for collection
          />
        </>
      )}
      {building.type==="static" && building?.namePosition && (
        <Text
          x={building.x + building.namePosition.x}
          y={building.y + building.namePosition.y}
          text={building.name.toUpperCase()}
          style={staticNameTextStyle}
        />
      )}
    </>
  );
};

// Memoize the Building component to prevent unnecessary re-renders
export default memo(Building, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.highlight === nextProps.highlight &&
    prevProps.building === nextProps.building &&
    JSON.stringify(prevProps.buildingData) ===
      JSON.stringify(nextProps.buildingData) &&
    prevProps.onCollect === nextProps.onCollect &&
    prevProps.onRepair === nextProps.onRepair &&
    prevProps.onOpenBuildDialog === nextProps.onOpenBuildDialog &&
    prevProps.onOpenUpgradeDialog === nextProps.onOpenUpgradeDialog &&
    prevProps.isCollecting === nextProps.isCollecting
  );
});
